/**
 * Swym Wishlist Plus bridge — SDK edition.
 *
 * Sole client-side facade over Swym's JS SDK. Exposes `window.EckList`,
 * a promise-based, event-emitting API used by every wishlist and
 * requisition surface in the theme.
 *
 * Design principles
 * -----------------
 *   Auth-aware routing. Swym has two behavioural modes:
 *     - Guest: single default wishlist, auto-created + managed by the
 *       SDK. Writes go through `_swat.addToWishList` /
 *       `_swat.removeFromWishList`. Named-list mutations (`createList`,
 *       `updateList`, `deleteList`, `addProductsToList`, …) are gated
 *       server-side by an ACL that returns `sw-unauth-acl` for anon
 *       sessions, so the bridge never calls them for guests.
 *     - Customer: full multi-list. `_swat.createList`,
 *       `_swat.addProductsToList`, `_swat.removeProductsFromList`,
 *       `_swat.updateList`, `_swat.deleteList`, `_swat.markListPublic`,
 *       `_swat.generateSharedListURL` — all work as documented.
 *
 *   REST for one read only. Swym's `_swat.fetchLists(cb, err)` fires the
 *   `/api/v3/lists/fetch-lists` HTTP call and receives a 200 response,
 *   but its JS callback never fires (empirically confirmed on paid tier,
 *   both anon and authenticated). We hit `/lists/fetch-lists` directly
 *   with the regid the SDK currently holds. All other reads (list
 *   details, single-list contents) use `_swat.fetchListDetails` which
 *   delivers callbacks correctly.
 *
 *   Guest → customer merge is Swym's job. When the shopper signs in the
 *   SDK materialises a customer-scoped regid and Swym server-side folds
 *   the anon default wishlist into the customer account. We listen for
 *   the session transition (`swym-authn` becoming `authn:true`) and
 *   flush cache + refetch.
 *
 * Emitted events (all on `document`)
 * ----------------------------------
 *   swym:ready              { swat }
 *   swym:degraded           { reason }
 *   swym:lists-changed      { lists }
 *   swym:products-changed   { lid, added, removed }
 *   swym:count-changed      { count }
 */
(function () {
  'use strict';

  const READY_TIMEOUT_MS = 8000;
  const COUNT_DEBOUNCE_MS = 200;
  const LIST_NAME_MIN = 3;
  const LIST_NAME_MAX = 50;
  const DEGRADED_ERROR = { code: 'swym-unavailable', message: 'Swym SDK unavailable' };
  const GUEST_ONLY_ERROR = { code: 'guests-cannot-multi-list', message: 'Multiple wishlists require sign-in' };

  let swat = null;
  let mode = 'initialising';

  let readyResolve;
  let readyReject;
  const readyPromise = new Promise((resolve, reject) => {
    readyResolve = resolve;
    readyReject = reject;
  });
  readyPromise.catch(() => {});

  const listsCache = new Map();          // lid -> List (may include listcontents)
  const itemsIndex = new Map();          // variantId (epi) -> Set<lid>
  let allListsFetched = false;
  let countCache = 0;
  let countDebounceTimer = null;

  // -------- Utilities --------

  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, {
      detail,
      bubbles: true,
      cancelable: false,
    }));
  }

  function listItems(list) {
    if (!list) return [];
    if (Array.isArray(list.listcontents)) return list.listcontents;
    if (Array.isArray(list.items)) return list.items;
    return [];
  }

  function rebuildItemsIndex() {
    itemsIndex.clear();
    for (const list of listsCache.values()) {
      for (const item of listItems(list)) {
        const epi = item && item.epi;
        if (!epi) continue;
        let lids = itemsIndex.get(epi);
        if (!lids) {
          lids = new Set();
          itemsIndex.set(epi, lids);
        }
        lids.add(list.lid);
      }
    }
  }

  function scheduleCountEvent() {
    if (countDebounceTimer !== null) clearTimeout(countDebounceTimer);
    countDebounceTimer = setTimeout(() => {
      countDebounceTimer = null;
      const next = itemsIndex.size;
      if (next !== countCache) {
        countCache = next;
        dispatch('swym:count-changed', { count: countCache });
      }
    }, COUNT_DEBOUNCE_MS);
  }

  function readCookie(name) {
    for (const c of document.cookie.split(';')) {
      const parts = c.trim().split('=');
      if (parts[0] !== name) continue;
      return parts.slice(1).join('=').replace(/^"|"$/g, '');
    }
    return null;
  }

  // -------- Auth state --------
  //
  // Guest vs signed-in decides which SDK path we take for a write. The
  // authoritative source is Swym's own `swym-authn` snapshot: it flips to
  // `authn: true` with a persistent regid the moment the SDK confirms a
  // customer session (which happens post-Storefront-login OR post-merge
  // on a guest that signed in mid-flow).

  function isSignedIn() {
    try {
      const raw = localStorage.getItem('swym-authn');
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      return !!(parsed && parsed.all && parsed.all.authn === true);
    } catch (e) {
      return false;
    }
  }

  // -------- SDK promise adapters --------

  function callSdk(methodName, ...args) {
    return new Promise((resolve, reject) => {
      if (!swat || typeof swat[methodName] !== 'function') {
        reject({ code: 'sdk-method-missing', method: methodName });
        return;
      }
      let settled = false;
      const onSuccess = (data) => { if (settled) return; settled = true; resolve(data); };
      const onError = (err) => { if (settled) return; settled = true; reject(err || { code: 'sdk-error', method: methodName }); };
      try {
        swat[methodName](...args, onSuccess, onError);
      } catch (e) {
        if (!settled) reject({ code: 'sdk-threw', method: methodName, cause: e });
      }
    });
  }

  // -------- Input validation --------

  // Swym's list-name API (createList / updateList) 400s on certain ASCII
  // punctuation. Empirically it accepts Unicode letters and marks, digits,
  // spaces and the punctuation subset below; everything else (percent,
  // asterisk, parentheses, question mark, angle brackets, quotes, brackets,
  // braces, pipe, caret, backslash, tilde, backtick, plus, equals, semicolon
  // and emoji) is rejected. Mirror that set client-side so callers fail fast
  // with a clear reason instead of an opaque error after a wasted round-trip.
  const LIST_NAME_ALLOWED_CHAR = /[\p{L}\p{M}\p{N} &'.,!@#$:_\/-]/u;

  function listNameInvalidChars(name) {
    const seen = [];
    for (const ch of String(name || '')) {
      if (!LIST_NAME_ALLOWED_CHAR.test(ch) && seen.indexOf(ch) === -1) seen.push(ch);
    }
    return seen;
  }

  function normaliseListName(name) {
    const trimmed = String(name || '').trim();
    if (trimmed.length < LIST_NAME_MIN || trimmed.length > LIST_NAME_MAX) {
      const err = new Error(`List name must be ${LIST_NAME_MIN}-${LIST_NAME_MAX} characters`);
      err.code = 'invalid-name';
      throw err;
    }
    const invalid = listNameInvalidChars(trimmed);
    if (invalid.length) {
      const err = new Error(`List name contains unsupported characters: ${invalid.join(' ')}`);
      err.code = 'invalid-chars';
      err.chars = invalid;
      throw err;
    }
    return trimmed;
  }

  function toProductPayload(product) {
    const epi = Number(product && product.variantId);
    const empi = Number(product && product.productId);
    const du = String((product && product.url) || '');
    if (!epi || !empi || !du) {
      const err = new Error('product requires variantId, productId, url');
      err.code = 'invalid-product';
      throw err;
    }
    const pageData = window.SwymPageData || {};
    const samePage = pageData.empi === empi;
    const payload = { epi, empi, du };
    if (product.qty != null) payload.qty = Number(product.qty);
    if (product.note) payload.note = String(product.note);
    if (product.source) payload.source = String(product.source);
    if (product.cprops && typeof product.cprops === 'object') payload.cprops = product.cprops;
    // Display fields (image, price, product + variant titles). Every caller
    // (wishlist-button, picker, remove-button, cart save-for-later) resolves
    // these from Liquid, so store them on the Swym item directly. Swym's own
    // server-side enrichment of `iu`/`pr` from the product URL never runs on a
    // password-gated store and is unreliable elsewhere, which left tiles for
    // products it couldn't crawl with no image — most visibly single-variant
    // products, whose variant carries no image of its own. Storing the
    // theme-supplied image guarantees every wishlist surface has one.
    const productImage = product.iu || product.image;
    if (productImage) payload.iu = String(productImage);
    const productPrice = product.pr != null ? product.pr : product.price;
    if (productPrice != null && productPrice !== '') payload.pr = Number(productPrice);
    const productTitle = product.dt || product.title;
    if (productTitle) payload.dt = String(productTitle);
    const variantTitle = product.vi || product.variantTitle;
    if (variantTitle) payload.vi = String(variantTitle);
    // A PDP with SwymPageData for THIS product is the most accurate source for
    // the pinned variant's image/price, so let it override the caller values.
    if (samePage) {
      if (pageData.pr != null) payload.pr = pageData.pr;
      if (pageData.iu != null) payload.iu = pageData.iu;
    }
    return payload;
  }

  // -------- REST fetch-lists fallback --------
  //
  // `_swat.fetchLists` never delivers its callback (SDK bug, paid tier
  // confirmed). We POST to the same endpoint the SDK would have,
  // passing the regid the SDK currently holds in the cookie so we read
  // under the same identity all other operations use.

  async function restFetchLists() {
    const sw = (swat && swat.session && swat.session.sw) || {};
    const swymHost = sw.swymHost;
    const pid = sw.pid || readCookie('swym-pid');
    const regid = readCookie('swym-swymRegid');
    const sessionid = (swat && swat.session && swat.session.sessionId) || null;
    if (!swymHost || !pid || !regid || !sessionid) {
      throw { code: 'auth-missing', message: 'Swym credentials not populated for REST fallback' };
    }
    const url = swymHost + '/api/v3/lists/fetch-lists?pid=' + encodeURIComponent(pid);
    const body = new URLSearchParams();
    body.set('regid', regid);
    body.set('sessionid', sessionid);
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
      },
      body: body.toString(),
    });
    if (!res.ok) throw { code: 'rest-fetch-lists-failed', status: res.status };
    const text = await res.text();
    if (!text) return [];
    try {
      const parsed = JSON.parse(text);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  // -------- Reads --------

  // Merge REST response into cache. REST returns server-authoritative
  // state, but a concurrent local write (SDK callback fired between
  // REST's request and response) may have already added an entry that
  // REST didn't know about. Merging preserves those entries; clearing
  // would silently drop them. Session transitions call `refreshAllLists`
  // instead when a full replacement is intended.
  function fetchAllLists() {
    return restFetchLists().then((lists) => {
      for (const list of lists) {
        if (list && list.lid) {
          const existing = listsCache.get(list.lid) || {};
          // Preserve local `listcontents` if REST didn't include them
          // (fetch-lists sometimes returns list metadata without items).
          const merged = Object.assign({}, existing, list);
          if (!Array.isArray(list.listcontents) && Array.isArray(existing.listcontents)) {
            merged.listcontents = existing.listcontents;
          }
          listsCache.set(list.lid, merged);
        }
      }
      allListsFetched = true;
      rebuildItemsIndex();
      dispatch('swym:lists-changed', { lists: Array.from(listsCache.values()) });
      scheduleCountEvent();
      return Array.from(listsCache.values());
    });
  }

  // Full replacement — used when identity changes (sign-in / sign-out /
  // guest→customer merge) and stale cache would leak the previous
  // identity's lists into the new one.
  function refreshAllLists() {
    listsCache.clear();
    allListsFetched = false;
    return fetchAllLists();
  }

  function fetchListById(lid) {
    if (!lid) return Promise.resolve(null);
    // If we already have a fresh snapshot with `listcontents`, return it
    // to avoid an unnecessary fetch (three surfaces on the wishlist detail
    // page listen to lists-changed; each firing a fetch spirals into
    // ERR_INSUFFICIENT_RESOURCES).
    if (allListsFetched && listsCache.has(lid) && Array.isArray(listsCache.get(lid).listcontents)) {
      return Promise.resolve(listsCache.get(lid));
    }
    return callSdk('fetchListDetails', { lid }).then((result) => {
      // Response shape: { list: {...}, items: [...] }. Normalise so callers
      // see `listcontents` alongside the list metadata.
      const listMeta = (result && result.list) || null;
      const items = (result && result.items) || [];
      if (!listMeta) return null;
      const merged = Object.assign({}, listMeta, { listcontents: items });
      listsCache.set(merged.lid, merged);
      rebuildItemsIndex();
      dispatch('swym:lists-changed', { lists: Array.from(listsCache.values()) });
      scheduleCountEvent();
      return merged;
    });
  }

  // Read-only fetch of a publicly-shared list by id. Powers the shared
  // wishlist view (`/pages/wishlist?share=<lid>`), which an unauthenticated
  // recipient can open. Swym gates the read server-side — a list becomes
  // retrievable by a non-owner session only after `markListPublic` (issued
  // by `shareList`). Unlike `fetchListById`, this deliberately does NOT
  // touch `listsCache`, rebuild the item index, or emit lists-changed/count
  // events: the list belongs to someone else and must never fold into the
  // viewer's own wishlist state (e.g. the header count).
  function getSharedList(lid) {
    if (!lid) return Promise.resolve(null);
    return callSdk('fetchListDetails', { lid }).then((result) => {
      const listMeta = (result && result.list) || null;
      const items = (result && result.items) || [];
      if (!listMeta) return null;
      return Object.assign({}, listMeta, { listcontents: items });
    });
  }

  // -------- Writes: signed-in path (multi-list) --------

  function createListAuthed(cfg) {
    const name = normaliseListName(cfg && cfg.name);
    const body = { lname: name, lty: (cfg && cfg.lty) || 'wl' };
    if (cfg && cfg.note) body.lnote = String(cfg.note);
    if (cfg && cfg.props && typeof cfg.props === 'object') body.lprops = cfg.props;
    return callSdk('createList', body).then((list) => {
      if (list && list.lid) {
        listsCache.set(list.lid, Object.assign({ listcontents: [] }, list));
      }
      dispatch('swym:lists-changed', { lists: Array.from(listsCache.values()) });
      return list;
    });
  }

  function renameListAuthed(lid, name) {
    const clean = normaliseListName(name);
    return callSdk('updateList', { lid, lname: clean }).then((updated) => {
      const cached = listsCache.get(lid) || {};
      const merged = Object.assign({}, cached, updated || {}, { lname: clean });
      listsCache.set(lid, merged);
      dispatch('swym:lists-changed', { lists: Array.from(listsCache.values()) });
      return merged;
    });
  }

  function deleteListAuthed(lid) {
    return callSdk('deleteList', lid).then(() => {
      listsCache.delete(lid);
      rebuildItemsIndex();
      dispatch('swym:lists-changed', { lists: Array.from(listsCache.values()) });
      scheduleCountEvent();
    });
  }

  function addProductAuthed(lid, product) {
    let payload;
    try { payload = toProductPayload(product); } catch (err) { return Promise.reject(err); }
    return callSdk('addProductsToList', lid, [payload]).then((result) => {
      // Response is an array of items or per-item errors.
      const items = Array.isArray(result) ? result : [];
      const failure = items.find((it) => it && it.hasError);
      if (failure) return Promise.reject({ code: 'add-failed', method: 'addProductsToList', response: failure });
      const added = items[0] || payload;
      const list = listsCache.get(lid);
      if (list) {
        /* Replace-or-append, never blind concat. Swym dedupes server-side
           and returns the EXISTING item when the product is already on the
           list, so appending put a second entry with the same `epi` into the
           cache. Every surface keys its `x-for` on `epi`, and a duplicate key
           takes Alpine's list renderer down with "Cannot read properties of
           undefined (reading 'after')" — the whole list stops rendering. The
           cached snapshot is what `fetchListById` serves back, so the
           corruption survived the re-hydrate the change event triggers. */
        const current = listItems(list);
        const at = current.findIndex((it) => it && Number(it.epi) === Number(added.epi));
        list.listcontents = at === -1
          ? current.concat([added])
          : current.map((it, i) => (i === at ? Object.assign({}, it, added) : it));
      }
      rebuildItemsIndex();
      dispatch('swym:products-changed', { lid, added: [added], removed: [] });
      scheduleCountEvent();
      return added;
    });
  }

  function removeProductAuthed(lid, product) {
    let payload;
    try { payload = toProductPayload(product); } catch (err) { return Promise.reject(err); }
    return callSdk('removeProductsFromList', lid, [payload]).then(() => {
      const list = listsCache.get(lid);
      if (list) {
        list.listcontents = listItems(list).filter((it) => !(it.epi === payload.epi && it.empi === payload.empi));
      }
      rebuildItemsIndex();
      dispatch('swym:products-changed', { lid, added: [], removed: [payload] });
      scheduleCountEvent();
    });
  }

  function shareListAuthed(lid) {
    if (!lid) return Promise.reject({ code: 'invalid-lid', method: 'shareList' });
    // Mark the list public so a non-owner (the share recipient) can read it
    // via `getSharedList`. We no longer call `generateSharedListURL` — that
    // returns Swym's hosted app-proxy page (`/apps/swymWishlist/shared/…`),
    // which renders a Liquid error on this theme (it hard-references a
    // `swymSnippet.liquid` the custom build doesn't ship). The caller builds
    // a theme-native `/pages/wishlist?share=<lid>` URL instead.
    return callSdk('markListPublic', lid).then(() => ({ lid }));
  }

  // -------- Writes: guest path (single default wishlist) --------
  //
  // The SDK's legacy `addToWishList` and `removeFromWishList` are the
  // only anon-safe writes. Their response shape is inconsistent —
  // sometimes the item payload includes the wishlist `lid`, sometimes
  // it doesn't (empirically, "already in list" and existing-item paths
  // return a stub without `lid`). Rather than depend on the response,
  // we resync from the server via REST fetch-lists after every guest
  // write. That's ONE additional REST call per write, but guests only
  // have a single default list so the payload is tiny and the extra
  // hop is worth the correctness guarantee.

  function syncAfterGuestWrite(kind, payload) {
    return fetchAllLists().then((lists) => {
      // Locate the guest's default wishlist (there's only one; Swym
      // auto-creates it on first add).
      const list = lists.find((l) => (l.lty || 'wl') === 'wl');
      const lid = list && list.lid;
      dispatch('swym:products-changed', {
        lid,
        added: kind === 'add' ? [payload] : [],
        removed: kind === 'remove' ? [payload] : [],
      });
      scheduleCountEvent();
      return list;
    });
  }

  function addProductGuest(product) {
    let payload;
    try { payload = toProductPayload(product); } catch (err) { return Promise.reject(err); }
    return callSdk('addToWishList', payload).then((result) => {
      return syncAfterGuestWrite('add', payload).then(() => result);
    });
  }

  function removeProductGuest(product) {
    let payload;
    try { payload = toProductPayload(product); } catch (err) { return Promise.reject(err); }
    return callSdk('removeFromWishList', payload).then((result) => {
      return syncAfterGuestWrite('remove', payload).then(() => result);
    });
  }

  // -------- Public API dispatch (guest vs authed) --------

  function createList(cfg) {
    if (!isSignedIn()) return Promise.reject(GUEST_ONLY_ERROR);
    return createListAuthed(cfg);
  }

  function renameList(lid, name) {
    if (!isSignedIn()) return Promise.reject(GUEST_ONLY_ERROR);
    return renameListAuthed(lid, name);
  }

  function deleteList(lid) {
    if (!isSignedIn()) return Promise.reject(GUEST_ONLY_ERROR);
    return deleteListAuthed(lid);
  }

  function addProduct(lid, product) {
    if (!isSignedIn()) return addProductGuest(product);
    if (lid) return addProductAuthed(lid, product);
    // Signed-in but no target list id - e.g. the wishlist button rendered in
    // guest mode before customer-account SSO settled and called addProduct(null).
    // Add to the default list (creating it if missing) instead of passing null
    // to addProductsToList, which Swym rejects with a 400.
    return getDefaultList({ createIfMissing: 'Favourites' }).then((list) => {
      if (!list || !list.lid) return Promise.reject({ code: 'no-default-list', method: 'addProduct' });
      return addProductAuthed(list.lid, product);
    });
  }

  function removeProduct(lid, product) {
    return isSignedIn() ? removeProductAuthed(lid, product) : removeProductGuest(product);
  }

  function updateProduct(lid, product) {
    // Product mutations (qty/note edits) are only meaningful for named
    // lists — a guest's default wishlist doesn't expose per-item edit
    // hooks. Keep the shape but no-op for guests to avoid an SDK ACL
    // rejection.
    if (!isSignedIn()) return Promise.resolve(null);
    let payload;
    try { payload = toProductPayload(product); } catch (err) { return Promise.reject(err); }
    /* Signature per the Swym extension bundle (`swym-ext-shopify.js`):
       `updateListItem(lid: string, item: object, success: fn, error: fn)`
       -- positional, and the lid is validated as a STRING, not folded into
       the item. Passing a single merged object throws "Invalid arguments
       supplied" out of `validateWishlistEnabled` before any request goes
       out; sending only `{ lid, epi }` was a no-op the other way, since
       `qty` and `note` ARE the mutation. Both were live bugs here: nothing
       called `updateProduct` until the requisition list's quantity
       write-back did. */
    return callSdk('updateListItem', String(lid), payload).then((result) => {
      const list = listsCache.get(lid);
      if (list) {
        // Merge the payload, not the SDK result — the two write paths above
        // return different shapes, and the payload is what was just sent.
        list.listcontents = listItems(list).map((it) => (it.epi === payload.epi ? Object.assign({}, it, payload) : it));
      }
      rebuildItemsIndex();
      dispatch('swym:products-changed', { lid, added: [], removed: [] });
      return result || payload;
    });
  }

  function moveProduct(fromLid, toLid, product) {
    if (!isSignedIn()) return Promise.reject(GUEST_ONLY_ERROR);
    return addProduct(toLid, product).then(() => removeProduct(fromLid, product));
  }

  function addProductToMany(lids, product) {
    if (!Array.isArray(lids) || lids.length === 0) return Promise.resolve([]);
    if (!isSignedIn()) return Promise.reject(GUEST_ONLY_ERROR);
    return Promise.all(lids.map((lid) => addProductAuthed(lid, product)));
  }

  function shareList(lid) {
    if (!isSignedIn()) return Promise.reject(GUEST_ONLY_ERROR);
    return shareListAuthed(lid);
  }

  // -------- Aggregations --------

  function ensureListsHydrated() {
    return ensureReady().then(() => (allListsFetched ? Array.from(listsCache.values()) : fetchAllLists()));
  }

  function isProductInAnyList(variantId) {
    const epi = Number(variantId);
    if (!epi) return Promise.resolve(false);
    return ensureListsHydrated().then(() => itemsIndex.has(epi));
  }

  function productListMembership(variantId) {
    const epi = Number(variantId);
    if (!epi) return Promise.resolve([]);
    return ensureListsHydrated().then(() => Array.from(itemsIndex.get(epi) || []));
  }

  function count() {
    return ensureListsHydrated().then(() => itemsIndex.size);
  }

  function getDefaultList(opts) {
    const createIfMissing = opts && opts.createIfMissing;
    return ensureListsHydrated().then((lists) => {
      const wishlistType = lists.find((l) => (l.lty || 'wl') === 'wl');
      if (wishlistType) return wishlistType;
      // Guests can't create named lists. Their default wishlist only
      // materialises when they add their first product via addToWishList.
      // Return null and let the caller trigger the add path.
      if (!isSignedIn()) return null;
      if (!createIfMissing) return null;
      return createListAuthed({ name: createIfMissing });
    });
  }

  // -------- Lifecycle --------

  function ensureReady() {
    if (mode === 'ready') return readyPromise;
    if (mode === 'degraded') return Promise.reject(DEGRADED_ERROR);
    return readyPromise;
  }

  function bindSdkEvents() {
    if (!swat || !swat.evtLayer || !swat.JSEvents) return;
    // Only listen for identity transitions (sign-in / sign-out / anon→
    // customer merge). Every mutation the bridge issues updates the
    // cache directly in the SDK-callback path, so subscribing to
    // `JSEvents.addToWishlist` / `removedFromWishlist` would only race
    // an unnecessary REST refetch against our own already-correct state
    // (the SDK dispatches those synchronously with the success callback).
    // Session events fire in several distinct scenarios and only ONE
    // of them warrants a full cache replacement:
    //   1. Anon session-establishment (guest's first write triggers
    //      sessionStarted) — must NOT clear cache; the write we just
    //      made is still pending resolution.
    //   2. Sign-in / sign-out — identity changed, cache from previous
    //      identity is invalid and must be replaced.
    // Distinguishing by comparing `swym-authn.all.authn` before and
    // after the event.
    let lastKnownAuthn = isSignedIn();
    const refresh = () => {
      const nowAuthn = isSignedIn();
      if (nowAuthn !== lastKnownAuthn) {
        lastKnownAuthn = nowAuthn;
        refreshAllLists().catch(() => {});
      } else {
        // Same identity — merge-fetch catches external changes without
        // dropping local writes.
        fetchAllLists().catch(() => {});
      }
    };
    const sessionEvents = [swat.JSEvents.sessionStarted, swat.JSEvents.sessionCreated, swat.JSEvents.registered].filter(Boolean);
    for (const evt of sessionEvents) {
      try { swat.evtLayer.addEventListener(evt, refresh); } catch (e) { /* silent */ }
    }
  }

  function onSwymReady(sdk) {
    if (mode !== 'initialising') return;
    swat = sdk;
    if (!swat) {
      transitionToDegraded('sdk-null');
      return;
    }
    // SwymCallbacks fires as soon as `_swat` is installed on window,
    // which is BEFORE the session handshake populates `session.sessionId`.
    // Fast-path when credentials are already there, otherwise wait for
    // the SDK's session-ready events.
    const ready = () => {
      const sessionId = swat.session && swat.session.sessionId;
      const swymHost = swat.session && swat.session.sw && swat.session.sw.swymHost;
      return !!(sessionId && swymHost);
    };
    if (ready()) return finaliseReady();
    const events = swat.JSEvents || {};
    const evtLayer = swat.evtLayer;
    const sessionEvents = [events.sessionStarted, events.sessionCreated, events.registered].filter(Boolean);
    if (evtLayer && typeof evtLayer.addEventListener === 'function' && sessionEvents.length > 0) {
      const onSessionEvent = () => {
        if (mode !== 'initialising') return;
        if (ready()) finaliseReady();
      };
      for (const evt of sessionEvents) {
        try { evtLayer.addEventListener(evt, onSessionEvent); } catch (e) { /* silent */ }
      }
    }
  }

  function finaliseReady() {
    if (mode !== 'initialising') return;
    mode = 'ready';
    dispatch('swym:ready', { swat });
    bindSdkEvents();
    // No initial fire-and-forget fetch. Consumers pull lists lazily via
    // `ensureListsHydrated` on their first read; that call resolves
    // before any user action can race it (button `syncFromBridge` on
    // mount awaits the same promise). Firing an initial fetch here
    // introduces a race where a user click that happens before the
    // response arrives can be blown away when the (stale) response
    // rewrites the cache.
    readyResolve(swat);
  }

  function transitionToDegraded(reason) {
    if (mode !== 'initialising') return;
    mode = 'degraded';
    console.warn(`[swym-bridge] Swym SDK unavailable (${reason}); wishlist surfaces will hide.`);
    dispatch('swym:degraded', { reason });
    readyReject(DEGRADED_ERROR);
  }

  // Guarded wrapper: every public method waits for ready first.
  function guarded(fn) {
    return function (...args) {
      return ensureReady().then(() => fn(...args));
    };
  }

  const EckList = {
    ready: () => readyPromise,
    getLists: guarded(() => (allListsFetched ? Promise.resolve(Array.from(listsCache.values())) : fetchAllLists())),
    getList: guarded(fetchListById),
    getSharedList: guarded(getSharedList),
    getDefaultList: guarded(getDefaultList),
    createList: guarded(createList),
    renameList: guarded(renameList),
    deleteList: guarded(deleteList),
    addProduct: guarded(addProduct),
    removeProduct: guarded(removeProduct),
    updateProduct: guarded(updateProduct),
    moveProduct: guarded(moveProduct),
    addProductToMany: guarded(addProductToMany),
    isProductInAnyList: guarded(isProductInAnyList),
    productListMembership: guarded(productListMembership),
    shareList: guarded(shareList),
    count: guarded(count),
    isSignedIn: () => isSignedIn(),
    listNameInvalidChars: (name) => listNameInvalidChars(name),
  };

  Object.defineProperty(EckList, 'mode', {
    get() { return mode; },
    enumerable: true,
    configurable: false,
  });

  window.EckList = Object.freeze(EckList);

  setTimeout(() => transitionToDegraded('timeout'), READY_TIMEOUT_MS);

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(onSwymReady);
})();
